import React, { useEffect } from 'react';
import { Button, Input, useToast } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useState } from 'react';
import { createEmbedding } from 'hooks/createEmbedding';
import { collection, getDocs } from "firebase/firestore";
import { db } from 'lib/firebase';
import similarity from 'compute-cosine-similarity';
import { PROTECTED } from 'lib/routes';
import { useGetTextBlocksFromMarkdownsFile } from 'hooks/notes';

import { Navigate } from 'react-router-dom';
import 'stylesheets/TreePilotMentor/TreePilotMentor.css';
import 'stylesheets/Skeletons/Writing.css'

import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import remarkGfm from 'remark-gfm';
import remarkGemoji from 'remark-gemoji';
import ButtonWithLoading from 'components/MainComponents/ButtonWithLoading/ButtonWithLoading';

const TreePilotMentor = () => {
  const {
    register,
    handleSubmit,
    reset,
  } = useForm();

  const [chatLog, setChatLog] = useState([]);
  const [isAiLoading, setAiLoading] = useState(false);
  const { setURLS, getTextBlocks } = useGetTextBlocksFromMarkdownsFile();
  const toast = useToast();



  const handleNewQuestion = async (data) => {
    setChatLog((prevChatLog) => [{ type: 'user', message: data.question }]);
    setAiLoading(true);
    const bestNotesRelated = await getBestsNotesRelated(data.question);
    const textBlocks = await getTextBlocks(bestNotesRelated);
    const textBlocksToAi = convertToFormattedString(textBlocks);
    sendMessage(data.question, textBlocksToAi);
    reset();
  };

  const getAverageS = (s) => {
    const sum = s.reduce((acumulador, num) => acumulador + num, 0);
    const quantityElements = s.length;
    const average = sum / quantityElements;
    return average;
  };

  const getBestsNotesRelated = async (input) => {
    const embeddedInput = await createEmbedding(input);
    const querySnapshot = await getDocs(collection(db, "notes"));

    let embeddingScores = [];

    querySnapshot.forEach((doc) => {
      let score = [];
      doc.data().tags.forEach(tag => {
        const key = Object.keys(tag)[0];
        const embeddingValue = tag[key];
        let s = similarity(embeddedInput.data[0].embedding, embeddingValue);
        score.push(s);
      });
      const averageScore = getAverageS(score);
      embeddingScores.push({
        link: `${PROTECTED}/comments/${doc.data().id}`,
        id: doc.data().id,
        text: doc.data().text,
        score: averageScore
      });
    });

    embeddingScores.sort((a, b) => b.score - a.score);
    const bestsNotesRelated = embeddingScores.slice(0, 5);
    return bestsNotesRelated;
  };

  const convertToFormattedString = (textBlocks) => {
    let formattedString = '';
    textBlocks.forEach((item) => {
      const { link, text } = item;
      formattedString += `[See Post](${link})\nInformation: ${text}\n`;
    });
    return formattedString;
  };

  const sendMessage = async (question, notesRelated) => {
    const url = 'https://api.openai.com/v1/chat/completions';
    const headers = {
      'Content-type': 'application/json',
      'Authorization': `Bearer ${process.env.REACT_APP_PUBLIC_OPENAI_API_KEY}`
    };
    const data = {
      model: "gpt-3.5-turbo",
      messages: [
        {
          "role": "system",
          content: `You are a mentor for the students in my application. You are going to help them find the best resources and ways to learn something. The application works with posts. You are going to receive 1 block of information from each of the first 5 posts related to the user's question. With that information, you have to recommend the user what to do, where to start and optional give them links to the posts you consider most useful (not necessarily the 5 or some). Do not include urls that are quoted within the information section of each block. If you plan to use urls, you should write it as it is in the information block in the part that says [See Post][]. If the question has nothing to do with the information blocks you are obligated to say: "Unfortunately we have not found related posts in the application but..." and answer as you would normally do. You are not allowed to give other urls that are not provided in the information blocks. The language of the answer will depend on the language in which the user is speaking to you. Each information block looks like this:\n\n` +
            // Example 1
            `Example of a information's block:` +
            `[See Post](protected/comments)\n` +
            `Information: Content of the post\n\n` +
            // Blocks of information
            `Real Data:` +
            `${notesRelated}`
        },
        { "role": "user", "content": question }
      ]
    };


    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(data)
      });

      const responseData = await response.json();
      setChatLog(prevChatLog => [{ type: "bot", message: responseData.choices[0].message.content }]);
      setAiLoading(false);
    } catch (error) {
      setAiLoading(false);
      toast({
        title: `Estamos recibiendo muchas peticiones, por favor inténtelo más tarde`,
        status: "error",
        isClosable: true,
        position: "top",
        duration: 5000,
      });
    }
  };

  return (
    <>
      <h2 className='treePilotMentor-title'>TreePilot Mentor</h2>
      <div className="treePilotMentor-chat">
        {chatLog.map((message, index) => (
          <div key={index} className='chat-log'>
            {message.type === 'user' ? <strong>User:</strong> : <strong>Bot:</strong>}
            {isAiLoading
              ?
              <div className='bot-writting'>
                <span></span>
                <span></span>
                <span></span>
              </div>
              : <ReactMarkdown className='line-break' children={message.message} remarkPlugins={[remarkGfm, remarkGemoji]} />}
          </div>
        ))}
      </div>
      <form onSubmit={handleSubmit(handleNewQuestion)}>
        <input className='treePilotMentor-input' placeholder='Prueba preguntando ¿Cómo puedo empezar a estudiar...?' {...register("question", { required: true })} />
        <ButtonWithLoading isLoading={isAiLoading} type='submit' title='Preguntar' className='button-mentor' w="min-content"/>
      </form>
    </>
  );
};

export default TreePilotMentor;
